import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThreeCircles } from 'react-loader-spinner';
import css from './ParentDashboard.module.css';
import MessagingSegment from './MessagingSegment';
import ChildExamPerformance from './ChildExamPerformance';
import TimetableGrid from './TimetableGrid';
import SchoolPWAInstallBanner from './SchoolPWAInstallBanner';
import SchoolPoweredFooter from './SchoolPoweredFooter';
import SchoolNotificationCenter from './SchoolNotificationCenter';
import { compressImageIfNeeded, validateImageFile } from '../../utils/imageUtils';
import SchoolPasswordInput from './SchoolPasswordInput';

// MUI icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import PersonIcon from '@mui/icons-material/Person';
import PaymentsIcon from '@mui/icons-material/Payments';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ForumIcon from '@mui/icons-material/Forum';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// ─── Constants ────────────────────────────────────────────────────────────────

const NAV = [
  { key: 'overview', label: 'Overview',    Icon: DashboardIcon },
  { key: 'children',  label: 'My Children', Icon: FamilyRestroomIcon },
  { key: 'fees',      label: 'Fees',        Icon: PaymentsIcon },
  { key: 'calendar',  label: 'Calendar',    Icon: CalendarMonthIcon },
  { key: 'timetable', label: 'Timetable',   Icon: ScheduleIcon     },
  { key: 'attendance',label: 'Attendance',  Icon: EventAvailableIcon },
  { key: 'messages',  label: 'Messages',    Icon: ForumIcon },
  { key: 'profile',   label: 'Profile',     Icon: PersonIcon },
];

function initials(firstName = '', lastName = '') {
  return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '?';
}

function hasPassportPhoto(profileImg) {
  const value = String(profileImg || '').trim();
  return Boolean(value && value !== '/uploads/avatar.png');
}

function ChildPassportAvatar({ firstName, lastName, profileImg, className, imageClassName }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = hasPassportPhoto(profileImg) && !imageFailed;

  return (
    <div className={className}>
      {showImage ? (
        <img
          src={profileImg}
          alt={`${firstName || 'Child'} ${lastName || ''} passport`}
          className={imageClassName}
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span>{initials(firstName, lastName)}</span>
      )}
    </div>
  );
}

// ─── Fetch helper ─────────────────────────────────────────────────────────────

function parentFetch(path, opts = {}) {
  const token = localStorage.getItem('school-token');
  const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const headers = {
    'x-access-token': token || '',
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...opts.headers,
  };
  return fetch(path, { ...opts, headers });
}

// ─── SEGMENT: Overview ────────────────────────────────────────────────────────

function OverviewSegment({ profile, students, loading, onGoToChildren, onSelectChild }) {
  return (
    <div>
      <div className={css.banner}>
        <div>
          <p className={css.bannerTitle}>Welcome back, {profile?.firstName || 'Parent'}!</p>
          <p className={css.bannerSub}>
            {profile?.schoolName ? `Keeping you connected with ${profile.schoolName}` : 'Track your children\'s progress here'}
          </p>
        </div>
      </div>

      <div className={css.statsRow}>
        {loading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className={css.statCard}>
              <div className={css.skel} style={{ height: 12, width: '60%', marginBottom: 10 }} />
              <div className={css.skel} style={{ height: 28, width: '40%' }} />
            </div>
          ))
        ) : (
          <>
            <div className={css.statCard}>
              <span className={css.statLabel}>Linked Children</span>
              <span className={css.statValue}>{students.length}</span>
              <span className={css.statSub}>at {profile?.schoolName || 'your school'}</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Term</span>
              <span className={css.statValue} style={{ fontSize: '1.2rem' }}>
                {profile?.term ? profile.term.charAt(0).toUpperCase() + profile.term.slice(1) : '—'}
              </span>
              <span className={css.statSub}>{profile?.session || ''}</span>
            </div>
          </>
        )}
      </div>

      <div className={css.card}>
        <div className={css.cardHeader}>
          <h3 className={css.cardTitle}>Your Children</h3>
        </div>
        {loading ? (
          <div className={css.skel} style={{ height: 60, width: '100%' }} />
        ) : students.length === 0 ? (
          <div className={css.emptyState}>
            <FamilyRestroomIcon className={css.emptyIcon} />
            <p className={css.emptyTitle}>No children linked yet</p>
            <p className={css.emptyDesc}>Ask your school admin to link your account to your child's student profile.</p>
          </div>
        ) : (
          <div className={css.childrenGrid}>
            {students.slice(0, 4).map(c => (
              <div
                key={String(c.membershipId)}
                className={`${css.childCard} ${css.childCardClickable}`}
                onClick={() => onSelectChild(c)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectChild(c);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`View ${c.firstName} ${c.lastName}'s exams and performance`}
              >
                <ChildPassportAvatar
                  key={`${c.membershipId}-${c.profileImg || 'none'}`}
                  firstName={c.firstName}
                  lastName={c.lastName}
                  profileImg={c.profileImg}
                  className={css.childAvatar}
                  imageClassName={css.childAvatarImage}
                />
                <div className={css.childCardBody}>
                  <p className={css.childName}>{c.firstName} {c.lastName}</p>
                  <p className={css.childMeta}>{c.className || 'Class not set'}</p>
                </div>
                <ChevronRightIcon className={css.childCardArrow} />
              </div>
            ))}
          </div>
        )}
        {students.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <button className={css.primaryBtn} onClick={onGoToChildren}>View All Children</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SEGMENT: My Children ─────────────────────────────────────────────────────

function ChildrenSegment({ students, loading, onSelectChild, onResetPassword }) {
  return (
    <div className={css.card}>
      <div className={css.cardHeader}>
        <h2 className={css.cardTitle}>My Children</h2>
      </div>
      {loading ? (
        <div className={css.skel} style={{ height: 60, width: '100%' }} />
      ) : students.length === 0 ? (
        <div className={css.emptyState}>
          <FamilyRestroomIcon className={css.emptyIcon} />
          <p className={css.emptyTitle}>No children linked yet</p>
          <p className={css.emptyDesc}>Ask your school admin to link your account to your child's student profile.</p>
        </div>
      ) : (
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Student</th>
                <th>Class</th>
                <th>Status</th>
                <th aria-label="View child" />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map(c => (
                <tr
                  key={String(c.membershipId)}
                  className={css.clickableRow}
                  onClick={() => onSelectChild && onSelectChild(c)}
                  onKeyDown={e => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectChild?.(c);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${c.firstName} ${c.lastName}'s exams and performance`}
                  title="View exams & performance"
                >
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <ChildPassportAvatar
                        key={`${c.membershipId}-${c.profileImg || 'none'}`}
                        firstName={c.firstName}
                        lastName={c.lastName}
                        profileImg={c.profileImg}
                        className={css.avatarInitials}
                        imageClassName={css.avatarInitialsImage}
                      />
                      <div>
                        <div style={{ fontWeight: 600 }}>{c.firstName} {c.lastName}</div>
                        <div style={{ fontSize: '0.78rem', color: '#90a4ae' }}>{c.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>{c.className || '—'}</td>
                  <td><span className={`${css.chip} ${css.chipTeal}`}>{c.status || 'active'}</span></td>
                  <td className={css.rowCue}>
                    <span>View</span>
                    <ChevronRightIcon fontSize="small" />
                  </td>
                  <td>
                    <button
                      type="button"
                      className={css.secondaryBtn}
                      onClick={event => { event.stopPropagation(); onResetPassword(c); }}
                    >
                      Reset password
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ResetChildPasswordModal({ student, onClose }) {
  const [mode, setMode] = useState('generate');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [parentPassword, setParentPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');
    const nextErrors = {};
    if (!parentPassword) nextErrors.parentPassword = 'Enter your parent password to confirm this reset.';
    if (mode === 'custom') {
      if (newPassword.length < 8) nextErrors.newPassword = 'Use at least 8 characters.';
      if (newPassword.length > 72) nextErrors.newPassword = 'Use no more than 72 characters.';
      if (newPassword !== confirmPassword) nextErrors.confirmPassword = 'Passwords do not match.';
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setLoading(true);
    try {
      const response = await parentFetch(`/parent/children/${student.membershipId}/reset-password`, {
        method: 'POST',
        headers: { 'x-parent-password': parentPassword },
        body: JSON.stringify({ mode, newPassword, confirmPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (data.field) setFieldErrors({ [data.field]: data.error || 'Please check this field.' });
        else if (data.code === 'WRONG_PARENT_PASSWORD') setFieldErrors({ parentPassword: data.error });
        setError(data.error || 'Could not reset this student password.');
        return;
      }
      setResult(data);
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyGeneratedPassword = async () => {
    try {
      await navigator.clipboard.writeText(result.generatedPassword);
      setCopied(true);
    } catch {
      setError('Could not copy the password. Select and copy it manually.');
    }
  };

  return (
    <div className={css.modalOverlay} onClick={() => !loading && onClose()}>
      <div className={css.modal} onClick={event => event.stopPropagation()}>
        <h2 className={css.modalTitle}>Reset {student.firstName}&apos;s password</h2>
        <p className={css.modalSub}>The old password will stop working and the student will be signed out of active sessions.</p>

        {student.sharedAccount && (
          <div className={css.resetWarning}>
            This Bubble Ai account is used outside this school or by another school. Resetting it changes the password everywhere.
          </div>
        )}

        {error && <div className={css.inlineError}>{error}</div>}
        {result ? (
          <>
            <div className={css.inlineSuccess}>
              Password reset successfully. The student is not required to change it at next login.
            </div>
            {result.generatedPassword && (
              <div className={css.generatedPassword}>
                <strong>New password</strong>
                <code>{result.generatedPassword}</code>
                <button type="button" className={css.secondaryBtn} onClick={copyGeneratedPassword}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            )}
            <div className={css.modalFooter}>
              <button type="button" className={css.primaryBtn} onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <form noValidate onSubmit={handleSubmit}>
            <fieldset className={css.resetModeGroup}>
              <legend className={css.formLabel}>New password</legend>
              <label className={css.resetModeOption}>
                <input type="radio" name="reset-mode" checked={mode === 'generate'} onChange={() => setMode('generate')} />
                Generate a temporary password
              </label>
              <label className={css.resetModeOption}>
                <input type="radio" name="reset-mode" checked={mode === 'custom'} onChange={() => setMode('custom')} />
                Set a password
              </label>
            </fieldset>

            {mode === 'custom' && (
              <>
                <div className={css.formGroup}>
                  <label className={css.formLabel} htmlFor="child-new-password">New password <span className={css.requiredMark}>*</span></label>
                  <SchoolPasswordInput
                    id="child-new-password"
                    className={`${css.formInput} ${fieldErrors.newPassword ? css.formInputError : ''}`}
                    value={newPassword}
                    onChange={event => { setNewPassword(event.target.value); setFieldErrors(current => ({ ...current, newPassword: '' })); }}
                    autoComplete="new-password"
                    aria-invalid={Boolean(fieldErrors.newPassword)}
                  />
                  {fieldErrors.newPassword && <div className={css.fieldError}>{fieldErrors.newPassword}</div>}
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel} htmlFor="child-confirm-password">Confirm password <span className={css.requiredMark}>*</span></label>
                  <SchoolPasswordInput
                    id="child-confirm-password"
                    className={`${css.formInput} ${fieldErrors.confirmPassword ? css.formInputError : ''}`}
                    value={confirmPassword}
                    onChange={event => { setConfirmPassword(event.target.value); setFieldErrors(current => ({ ...current, confirmPassword: '' })); }}
                    autoComplete="new-password"
                    aria-invalid={Boolean(fieldErrors.confirmPassword)}
                  />
                  {fieldErrors.confirmPassword && <div className={css.fieldError}>{fieldErrors.confirmPassword}</div>}
                </div>
              </>
            )}

            <div className={css.formGroup}>
              <label className={css.formLabel} htmlFor="parent-confirm-password">Your parent password <span className={css.requiredMark}>*</span></label>
              <SchoolPasswordInput
                id="parent-confirm-password"
                className={`${css.formInput} ${fieldErrors.parentPassword ? css.formInputError : ''}`}
                value={parentPassword}
                onChange={event => { setParentPassword(event.target.value); setFieldErrors(current => ({ ...current, parentPassword: '' })); }}
                autoComplete="current-password"
                aria-invalid={Boolean(fieldErrors.parentPassword)}
              />
              {fieldErrors.parentPassword && <div className={css.fieldError}>{fieldErrors.parentPassword}</div>}
            </div>
            <p className={css.resetPasswordHint}>The new password is shown only here. It will not be emailed. You can share it securely with your child.</p>
            <div className={css.modalFooter}>
              <button type="button" className={css.secondaryBtn} onClick={onClose} disabled={loading}>Cancel</button>
              <button type="submit" className={css.primaryBtn} disabled={loading}>
                {loading ? 'Resetting…' : 'Reset password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── SEGMENT: Fees ────────────────────────────────────────────────────────────

const INVOICE_STATUS_STYLES = {
  unpaid:  { background: '#fce4ec', color: '#c62828' },
  partial: { background: '#fff3e0', color: '#e65100' },
  paid:    { background: '#d1fae5', color: '#065f46' },
  overdue: { background: '#fce4ec', color: '#c62828' },
  waived:  { background: '#f0f4f8', color: '#607d8b' },
  pending_selection: { background: '#fff8e1', color: '#8d6e00' },
};

const money = value => `₦${Number(value || 0).toLocaleString('en-NG')}`;
const statusLabel = status => status === 'pending_selection' ? 'Selection needed' : String(status || '').replace(/_/g, ' ');
const hasPricingOptions = invoice => (invoice.lineItems || []).some(lineItem => lineItem.pricingMode === 'options' || (lineItem.pricingOptionsSnapshot || []).length > 0);

function FeeSelectionModal({ invoice, preview, onClose, onConfirm, confirming, error }) {
  const [selections, setSelections] = useState(() => Object.fromEntries(
    (preview.fees || [])
      .filter(fee => fee.pricingMode === 'options')
      .map(fee => [String(fee.feeId), fee.selectedOptionKey || ''])
  ));
  const optionFees = (preview.fees || []).filter(fee => fee.pricingMode === 'options');
  const flatAmount = (preview.fees || [])
    .filter(fee => fee.pricingMode !== 'options')
    .reduce((sum, fee) => sum + Number(fee.amount || 0), 0);
  const totals = optionFees.reduce((result, fee) => {
    const selectedKey = selections[String(fee.feeId)] || '';
    const selected = fee.options.find(option => option.key === selectedKey);
    const minimum = Math.min(...fee.options.map(option => Number(option.amount || 0)));
    const maximum = Math.max(...fee.options.map(option => Number(option.amount || 0)));
    result.minimum += selected ? selected.amount : minimum;
    result.maximum += selected ? selected.amount : maximum;
    result.total += selected ? selected.amount : 0;
    if (!selected) result.missing += 1;
    return result;
  }, { minimum: flatAmount, maximum: flatAmount, total: 0, missing: 0 });
  const selectedTotal = flatAmount + totals.total;
  const minimumTotal = flatAmount + totals.minimum;
  const maximumTotal = flatAmount + totals.maximum;
  const amountPaid = Number(preview.amountPaid ?? invoice.amountPaid ?? 0);
  const canChange = preview.canChangeSelection !== false;
  const complete = totals.missing === 0;
  const belowPaid = complete && selectedTotal < amountPaid;
  const selectionPayload = optionFees.map(fee => ({ feeId: fee.feeId, optionKey: selections[String(fee.feeId)] }));

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} ${css.feeSelectionModal} BubblePopIn`} onClick={event => event.stopPropagation()}>
        <h2 className={css.modalTitle}>{invoice.status === 'pending_selection' ? 'Choose fee options' : 'Change fee options'}</h2>
        <p className={css.modalSub}>
          {invoice.student?.firstName} {invoice.student?.lastName} · <span style={{ textTransform: 'capitalize' }}>{invoice.term}</span> term · {invoice.session}
        </p>
        {error && <div className={css.inlineError}>{error}</div>}
        {!canChange && <div className={css.inlineError}>This invoice can no longer be changed because it is settled.</div>}
        {canChange && invoice.status !== 'pending_selection' && (
          <div className={css.selectionRule}>You can change these choices until the invoice is fully paid. Amount already paid: <strong>{money(amountPaid)}</strong>.</div>
        )}

        <div className={css.feeSelectionList}>
          {(preview.fees || []).map(fee => {
            if (fee.pricingMode !== 'options') {
              return <div key={fee.feeId} className={css.feeSelectionFixed}><span>{fee.name}</span><strong>{money(fee.amount)}</strong></div>;
            }
            return (
              <div key={fee.feeId} className={css.feeSelectionRow}>
                <div>
                  <strong>{fee.name}</strong>
                  <span className={css.feeSelectionMeta}>{fee.category || 'Fee'} · choose one</span>
                </div>
                <select
                  className={css.formSelect}
                  value={selections[String(fee.feeId)] || ''}
                  onChange={event => setSelections(current => ({ ...current, [String(fee.feeId)]: event.target.value }))}
                  disabled={!canChange || confirming}
                  aria-label={`Pricing option for ${fee.name}`}
                >
                  <option value="">Choose an option</option>
                  {fee.options.map(option => <option key={option.key} value={option.key}>{option.label} · {money(option.amount)}</option>)}
                </select>
              </div>
            );
          })}
        </div>

        <div className={css.feeSelectionSummary}>
          <div><span>{complete ? 'Exact total' : 'Estimated total range'}</span><strong>{complete ? money(selectedTotal) : `${money(minimumTotal)} – ${money(maximumTotal)}`}</strong></div>
          <div><span>Amount paid</span><strong>{money(amountPaid)}</strong></div>
          <div><span>{complete ? 'Balance after selection' : 'Balance'}</span><strong>{complete ? money(Math.max(0, selectedTotal - amountPaid)) : 'Choose all options'}</strong></div>
        </div>
        {belowPaid && <div className={css.inlineError}>This selection totals less than the amount already paid. Choose a higher-priced option.</div>}
        {!complete && <div className={css.selectionHint}>Choose one option for each highlighted fee to see the exact total before payment.</div>}

        <div className={css.modalFooter}>
          <button className={css.secondaryBtn} onClick={onClose} disabled={confirming}>Close</button>
          {canChange && (
            <button className={css.primaryBtn} onClick={() => onConfirm(selectionPayload)} disabled={confirming || !complete || belowPaid}>
              {confirming ? <ThreeCircles height="16" width="16" color="#fff" visible /> : invoice.status === 'pending_selection' ? 'Confirm choices' : 'Save choices'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function InvoiceDetailsModal({ invoice, onClose, onPay, onSelectOptions, paying, payError }) {
  const isPendingSelection = invoice.status === 'pending_selection';
  const isPayable = !isPendingSelection && invoice.status !== 'paid' && invoice.status !== 'waived' && invoice.balance > 0;
  const canChangeOptions = hasPricingOptions(invoice) && ['unpaid', 'partial', 'overdue', 'pending_selection'].includes(invoice.status);

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Invoice Details</h2>
        <p className={css.modalSub}>
          {invoice.student?.firstName} {invoice.student?.lastName} · <span style={{ textTransform: 'capitalize' }}>{invoice.term}</span> term · {invoice.session}
        </p>

        {payError && <div className={css.inlineError}>{payError}</div>}

        <div className={css.invoiceDetailRow}>
          <span className={css.invoiceDetailLabel}>Status</span>
          <span className={css.chip} style={INVOICE_STATUS_STYLES[invoice.isOverdue ? 'overdue' : invoice.status] || INVOICE_STATUS_STYLES.unpaid}>
            {invoice.isOverdue ? 'Overdue' : statusLabel(invoice.status)}
          </span>
        </div>
        {invoice.dueDate && (
          <div className={css.invoiceDetailRow}>
            <span className={css.invoiceDetailLabel}>Due Date</span>
            <span className={css.invoiceDetailValue}>{new Date(invoice.dueDate).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
        )}

        <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0C1E28', margin: '18px 0 4px' }}>Fee Breakdown</h3>
        <table className={css.lineItemsTable}>
          <tbody>
            {(invoice.lineItems || []).map((li, i) => (
              <tr key={i}>
                <td style={{ color: '#0C1E28' }}>{li.label}{li.pricingOptionLabel ? <span className={css.selectedOptionLabel}> · {li.pricingOptionLabel}</span> : li.pricingMode === 'options' ? <span className={css.selectedOptionLabel}> · option needed</span> : null}</td>
                <td style={{ textAlign: 'right', color: '#0C1E28' }}>{li.pricingMode === 'options' && !li.pricingOptionLabel ? '—' : money(li.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={css.invoiceDetailRow}>
          <span className={css.invoiceDetailLabel}>Total</span>
          <span className={css.invoiceDetailValue}>{isPendingSelection ? `${money(invoice.minimumTotalAmount)} – ${money(invoice.maximumTotalAmount)}` : money(invoice.totalAmount)}</span>
        </div>
        <div className={css.invoiceDetailRow}>
          <span className={css.invoiceDetailLabel}>Amount Paid</span>
          <span className={css.invoiceDetailValue}>₦{Number(invoice.amountPaid || 0).toLocaleString('en-NG')}</span>
        </div>
        <div className={css.invoiceDetailRow}>
          <span className={css.invoiceDetailLabel}>Balance</span>
          <span className={css.invoiceDetailValue}>{isPendingSelection ? 'Pending selection' : money(invoice.balance)}</span>
        </div>
        {isPendingSelection && <div className={css.selectionRule}>Choose one pricing option for each applicable fee. The exact invoice total will be calculated before payment.</div>}
        {invoice.status === 'waived' && invoice.waivedReason && (
          <div className={css.invoiceDetailRow}>
            <span className={css.invoiceDetailLabel}>Waived Reason</span>
            <span className={css.invoiceDetailValue}>{invoice.waivedReason}</span>
          </div>
        )}

        <div className={css.modalFooter}>
          <button className={css.secondaryBtn} onClick={onClose} disabled={paying}>Close</button>
          {canChangeOptions && (
            <button className={css.secondaryBtn} onClick={() => onSelectOptions(invoice)} disabled={paying}>
              {isPendingSelection ? 'Choose fee options' : 'Change options'}
            </button>
          )}
          {isPayable && (
            <button className={css.primaryBtn} onClick={() => onPay(invoice)} disabled={paying}>
              {paying ? <ThreeCircles height="16" width="16" color="#fff" visible /> : 'Pay Now'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FeesSegment() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [payingId, setPayingId] = useState(null);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [payError, setPayError] = useState('');
  const [selectionTarget, setSelectionTarget] = useState(null);
  const [selectionLoadingId, setSelectionLoadingId] = useState(null);
  const [confirmingSelectionId, setConfirmingSelectionId] = useState(null);
  const [selectionError, setSelectionError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await parentFetch('/parent/fees/invoices');
      const data = await res.json();
      if (res.ok) setInvoices(data.invoices || []);
      else setError(data.error || 'Failed to load invoices.');
    } catch {
      setError('Connection error.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePay = async (invoice) => {
    setPayError('');
    setPayingId(invoice._id);
    try {
      const res = await parentFetch(`/parent/fees/${invoice._id}/pay`, { method: 'POST' });
      const data = await res.json();
      if (res.ok && data.link) {
        window.location.href = data.link;
      } else {
        setPayError(data.error || 'Could not start payment.');
        setPayingId(null);
      }
    } catch {
      setPayError('Connection error.');
      setPayingId(null);
    }
  };

  const openSelection = async invoice => {
    setSelectionError('');
    setSelectionLoadingId(invoice._id);
    try {
      const params = new URLSearchParams({ term: invoice.term, session: invoice.session });
      const res = await parentFetch(`/parent/fees/preview/${invoice.membershipId}?${params.toString()}`);
      const data = await res.json();
      if (res.ok) setSelectionTarget({ invoice, preview: data });
      else setSelectionError(data.error || 'Could not load fee options.');
    } catch {
      setSelectionError('Connection error.');
    } finally {
      setSelectionLoadingId(null);
    }
  };

  const confirmSelection = async selections => {
    if (!selectionTarget) return;
    const { invoice } = selectionTarget;
    setSelectionError('');
    setConfirmingSelectionId(invoice._id);
    try {
      const res = await parentFetch(`/parent/fees/preview/${invoice.membershipId}`, {
        method: 'POST',
        body: JSON.stringify({ term: invoice.term, session: invoice.session, selections }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSelectionError(data.error || 'Could not save fee options.');
        return;
      }
      setSelectionTarget(null);
      await load();
    } catch {
      setSelectionError('Connection error.');
    } finally {
      setConfirmingSelectionId(null);
    }
  };

  return (
    <div className={css.card}>
      <div className={css.cardHeader}>
        <h2 className={css.cardTitle}>School Fees</h2>
      </div>
      {error && <div className={css.inlineError} style={{ marginBottom: 12 }}>{error}</div>}
      {loading ? (
        <div className={css.skel} style={{ height: 60, width: '100%' }} />
      ) : invoices.length === 0 ? (
        <div className={css.emptyState}>
          <PaymentsIcon className={css.emptyIcon} />
          <p className={css.emptyTitle}>No invoices yet</p>
          <p className={css.emptyDesc}>Your school hasn't generated any fee invoices for your children yet.</p>
        </div>
      ) : (
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Student</th><th>Term/Session</th><th>Total</th><th>Balance</th><th>Status</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv._id}>
                  <td>{inv.student?.firstName} {inv.student?.lastName}</td>
                  <td style={{ fontSize: '0.78rem', color: '#607d8b' }}><span style={{ textTransform: 'capitalize' }}>{inv.term}</span> · {inv.session}</td>
                  <td>{inv.status === 'pending_selection' ? `${money(inv.minimumTotalAmount)} – ${money(inv.maximumTotalAmount)}` : money(inv.totalAmount)}</td>
                  <td>{inv.status === 'pending_selection' ? '—' : money(inv.balance)}</td>
                  <td>
                    <span className={css.chip} style={INVOICE_STATUS_STYLES[inv.isOverdue ? 'overdue' : inv.status] || INVOICE_STATUS_STYLES.unpaid}>
                      {inv.isOverdue ? 'Overdue' : statusLabel(inv.status)}
                    </span>
                  </td>
                  <td>
                    <button className={css.secondaryBtn} style={{ padding: '6px 14px', fontSize: '0.8rem' }} onClick={() => {
                      setPayError('');
                      if (inv.status === 'pending_selection') openSelection(inv);
                      else setDetailsTarget(inv);
                    }} disabled={selectionLoadingId === inv._id}>
                      {selectionLoadingId === inv._id ? 'Loading…' : inv.status === 'pending_selection' ? 'Choose options' : 'View Details'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailsTarget && (
        <InvoiceDetailsModal
          invoice={detailsTarget}
          onClose={() => setDetailsTarget(null)}
          onPay={handlePay}
          onSelectOptions={invoice => { setDetailsTarget(null); openSelection(invoice); }}
          paying={payingId === detailsTarget._id}
          payError={payError}
        />
      )}
      {selectionError && !selectionTarget && <div className={css.inlineError} style={{ marginTop: 12 }}>{selectionError}</div>}
      {selectionTarget && (
        <FeeSelectionModal
          invoice={selectionTarget.invoice}
          preview={selectionTarget.preview}
          onClose={() => setSelectionTarget(null)}
          onConfirm={confirmSelection}
          confirming={confirmingSelectionId === selectionTarget.invoice._id}
          error={selectionError}
        />
      )}
    </div>
  );
}

// ─── SEGMENT: Calendar (read-only, audience-filtered, Premium-gated) ─────────

function calEventTypeBadgeClass(type) {
  return {
    exam: css.calBadgeExam, holiday: css.calBadgeHoliday, meeting: css.calBadgeMeeting,
    deadline: css.calBadgeDeadline, activity: css.calBadgeActivity,
  }[type] || css.calBadgeOther;
}
function fmtCalDateHeading(d) {
  return new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtCalTime(d) {
  return new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function CalendarSegment({ profile }) {
  const managementEnabled = profile?.subscription?.plan === 'premium' && profile?.subscription?.schoolManagementEnabled !== false;
  const [monthDate, setMonthDate] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; });
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!managementEnabled) { setLoading(false); return; }
    setLoading(true);
    const from = monthDate.toISOString().slice(0, 10);
    const to = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).toISOString().slice(0, 10);
    parentFetch(`/parent/calendar?from=${from}&to=${to}`)
      .then(r => r.json())
      .then(data => { setEvents(data.events || []); setError(data.error || ''); })
      .catch(() => setError('Connection error.'))
      .finally(() => setLoading(false));
  }, [monthDate, managementEnabled]);

  if (!managementEnabled) {
    return (
      <div className={css.card}>
        <div className={css.premiumLockBanner}>
          🔒 Calendar &amp; Events is a <strong>Premium</strong> feature. Ask your school admin to upgrade the plan to unlock it.
        </div>
      </div>
    );
  }

  const grouped = {};
  events.forEach(ev => {
    const key = new Date(ev.startDate).toISOString().slice(0, 10);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(ev);
  });
  const sortedKeys = Object.keys(grouped).sort();

  return (
    <div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      <div className={css.card}>
        <div className={css.calHeaderRow}>
          <button className={css.calNavBtn} onClick={() => setMonthDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}>‹</button>
          <span className={css.calMonthLabel}>{monthDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
          <button className={css.calNavBtn} onClick={() => setMonthDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}>›</button>
        </div>

        {loading ? (
          <div className={css.skel} style={{ width: '100%', height: 80, borderRadius: 10 }} />
        ) : sortedKeys.length === 0 ? (
          <div className={css.emptyState}>
            <p className={css.emptyDesc}>No events this month.</p>
          </div>
        ) : (
          <div className={css.calAgenda}>
            {sortedKeys.map(dateKey => (
              <div key={dateKey} className={css.calDayGroup}>
                <div className={css.calDayHeading}>{fmtCalDateHeading(dateKey)}</div>
                {grouped[dateKey].map(ev => (
                  <div key={ev._id} className={css.calEventCard}>
                    <span className={`${css.calBadge} ${calEventTypeBadgeClass(ev.type)}`}>{ev.type}</span>
                    <div>
                      <div className={css.calEventTitle}>{ev.title}</div>
                      <div className={css.calEventMeta}>
                        {!ev.allDay && `${fmtCalTime(ev.startDate)}${ev.endDate ? ` – ${fmtCalTime(ev.endDate)}` : ''} · `}
                        {ev.audience === 'class'
                          ? ((ev.classIds || []).map(c => c.name).join(', ') || 'Class')
                          : ev.audience.charAt(0).toUpperCase() + ev.audience.slice(1)}
                        {ev.location ? ` · ${ev.location}` : ''}
                      </div>
                      {ev.description && <div className={css.calEventDesc}>{ev.description}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SEGMENT: Attendance (per-child, read-only) ──────────────────────────────

function AttendanceSegment({ profile, students }) {
  const managementEnabled = profile?.subscription?.plan === 'premium' && profile?.subscription?.schoolManagementEnabled !== false;
  const [membershipId, setMembershipId] = useState('');
  const [stats, setStats]     = useState({ present: 0, total: 0, rate: null });
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!membershipId && students.length > 0) setMembershipId(students[0].membershipId);
  }, [students, membershipId]);

  useEffect(() => {
    if (!managementEnabled || !membershipId) return;
    setLoading(true);
    parentFetch(`/parent/attendance?membershipId=${membershipId}`)
      .then(r => r.json())
      .then(data => {
        setStats(data.stats || { present: 0, total: 0, rate: null });
        setRecords(data.records || []);
        setError(data.error || '');
      })
      .catch(() => setError('Connection error.'))
      .finally(() => setLoading(false));
  }, [membershipId, managementEnabled]);

  if (!managementEnabled) {
    return (
      <div className={css.card}>
        <div className={css.premiumLockBanner}>
          🔒 Attendance is a <strong>Premium</strong> feature. Ask your school admin to upgrade the plan to unlock it.
        </div>
      </div>
    );
  }

  return (
    <div className={css.card}>
      <div className={css.cardHeader}>
        <h2 className={css.cardTitle}>Attendance</h2>
        {students.length > 1 && (
          <select className={css.filterSelect} value={membershipId} onChange={e => setMembershipId(e.target.value)}>
            {students.map(s => <option key={s.membershipId} value={s.membershipId}>{s.firstName} {s.lastName}</option>)}
          </select>
        )}
      </div>
      {error && <div className={css.inlineError} style={{ marginBottom: 12 }}>{error}</div>}
      {students.length === 0 ? (
        <div className={css.emptyState}>
          <p className={css.emptyDesc}>No children linked to your account yet.</p>
        </div>
      ) : loading ? (
        <div className={css.skel} style={{ height: 60, width: '100%' }} />
      ) : (
        <>
          <div style={{ marginBottom: 20, maxWidth: 220 }}>
            <span className={css.chip} style={{ background: stats.rate == null ? '#f0f4f8' : stats.rate < 75 ? '#fce4ec' : '#d1fae5', color: stats.rate == null ? '#607d8b' : stats.rate < 75 ? '#c62828' : '#065f46', fontSize: '1rem', padding: '6px 14px' }}>
              {stats.rate == null ? 'No records yet' : `${stats.rate}% attendance`}
            </span>
            <div style={{ fontSize: '0.78rem', color: '#78909c', marginTop: 6 }}>{stats.present}/{stats.total} periods present</div>
          </div>
          {records.length === 0 ? (
            <div className={css.emptyState}>
              <p className={css.emptyDesc}>No attendance records yet.</p>
            </div>
          ) : (
            <div className={css.tableWrap}>
              <table className={css.table}>
                <thead>
                  <tr><th>Date</th><th>Subject</th><th>Period</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i}>
                      <td>{r.date}</td>
                      <td>{r.subjectName || '—'}</td>
                      <td>{r.periodLabel || '—'}</td>
                      <td style={{ textTransform: 'capitalize', fontWeight: 600, color: r.status === 'present' ? '#2e7d32' : r.status === 'absent' ? '#c62828' : r.status === 'late' ? '#e65100' : '#546e7a' }}>
                        {r.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── SEGMENT: Profile ─────────────────────────────────────────────────────────

function ProfileSegment({ profile, onProfileUpdated }) {
  const [form, setForm] = useState({
    email: profile?.email || '',
    firstName: profile?.firstName || '',
    lastName: profile?.lastName || '',
    phone: profile?.phone || '',
    dob: profile?.dob || '',
    address: profile?.address || '',
    city: profile?.city || '',
    stateRegion: profile?.stateRegion || '',
    postalCode: profile?.postalCode || '',
    country: profile?.country || '',
    countryOfOrigin: profile?.countryOfOrigin || '',
    occupation: profile?.occupation || '',
    otherGuardianName: profile?.otherGuardianName || '',
    otherGuardianRelationship: profile?.otherGuardianRelationship || '',
    otherGuardianPhone: profile?.otherGuardianPhone || '',
    otherGuardianEmail: profile?.otherGuardianEmail || '',
    otherGuardianAddress: profile?.otherGuardianAddress || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoSuccess, setPhotoSuccess] = useState('');
  const fileInputRef = useRef(null);
  const [emailChangeStep, setEmailChangeStep] = useState('idle');
  const [pendingEmail, setPendingEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [emailChangeSaving, setEmailChangeSaving] = useState(false);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleFileChange = async event => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    setPhotoError('');
    setPhotoSuccess('');
    const validation = validateImageFile(file, 2);
    if (!validation.valid) {
      setPhotoError(validation.error);
      return;
    }

    let finalFile = file;
    if (file.size > 2 * 1024 * 1024) {
      try {
        const result = await compressImageIfNeeded(file, 2);
        finalFile = result.file;
      } catch {
        setPhotoError('Image compression failed. Try a smaller image.');
        return;
      }
    }

    if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setSelectedFile(finalFile);
    setPreviewUrl(URL.createObjectURL(finalFile));
  };

  const uploadPassport = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setPhotoError('');
    setPhotoSuccess('');
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await parentFetch('/parent/profile-image', { method: 'POST', body: formData });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.profileImg) {
        setPhotoError(data.error || 'Upload failed. Please try again.');
        return;
      }
      if (onProfileUpdated) onProfileUpdated({ ...profile, profileImg: data.profileImg });
      setSelectedFile(null);
      if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
      setPhotoSuccess('Passport image updated.');
    } catch {
      setPhotoError('Connection error. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setSaving(true);
    const nextEmail = String(form.email || '').trim().toLowerCase();
    const currentEmail = String(profile?.email || '').trim().toLowerCase();
    try {
      if (nextEmail !== currentEmail) {
        const res = await parentFetch('/parent/profile/email-change/request', {
          method: 'POST',
          body: JSON.stringify({ email: nextEmail }),
        });
        const data = await res.json();
        if (res.ok) {
          setPendingEmail(data.email || nextEmail);
          setVerificationCode('');
          setEmailChangeStep('code');
          setSuccess('We sent a verification code to your new email address.');
        } else {
          setError(data.error || 'Could not send a verification code.');
        }
        return;
      }
      const res = await parentFetch('/parent/profile', { method: 'PATCH', body: JSON.stringify(form) });
      const data = await res.json();
      if (res.ok) {
        setSuccess('Profile updated successfully.');
        if (onProfileUpdated) onProfileUpdated(data.profile);
      } else {
        setError(data.error || 'Update failed.');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyEmailChange = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setEmailChangeSaving(true);
    try {
      const res = await parentFetch('/parent/profile/email-change/verify', {
        method: 'POST',
        body: JSON.stringify({ email: pendingEmail, code: verificationCode, profile: form }),
      });
      const data = await res.json();
      if (res.ok) {
        setEmailChangeStep('idle');
        setPendingEmail('');
        setVerificationCode('');
        setSuccess('Email and profile updated successfully.');
        if (onProfileUpdated) onProfileUpdated(data.profile);
      } else {
        setError(data.error || 'Email verification failed.');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setEmailChangeSaving(false);
    }
  };

  const cancelEmailChange = () => {
    setEmailChangeStep('idle');
    setPendingEmail('');
    setVerificationCode('');
    setForm(current => ({ ...current, email: profile?.email || '' }));
    setError('');
    setSuccess('');
  };

  const [pwd, setPwd] = useState({ currentPassword: '', newPassword: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdError(''); setPwdSuccess(''); setPwdSaving(true);
    try {
      const res = await parentFetch('/parent/change-password', { method: 'POST', body: JSON.stringify(pwd) });
      const data = await res.json();
      if (res.ok) {
        setPwdSuccess('Password changed successfully.');
        setPwd({ currentPassword: '', newPassword: '' });
      } else {
        setPwdError(data.error || 'Password change failed.');
      }
    } catch {
      setPwdError('Connection error. Please try again.');
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <>
      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>Profile</h2>
        </div>
        <div className={css.profilePhotoPanel}>
          <div className={css.profilePhotoPreview}>
            {hasPassportPhoto(previewUrl || profile?.profileImg) ? (
              <img src={previewUrl || profile.profileImg} alt="Parent passport" className={css.profilePhotoImage} />
            ) : (
              <span>{initials(profile?.firstName, profile?.lastName)}</span>
            )}
          </div>
          <div className={css.profilePhotoContent}>
            <div className={css.profilePhotoTitle}>Passport Photo</div>
            <p className={css.profilePhotoHint}>Use a clear passport-style photo for your parent profile.</p>
            <input ref={fileInputRef} type="file" accept="image/*" className={css.hiddenFileInput} onChange={handleFileChange} />
            <div className={css.profilePhotoActions}>
              <button type="button" className={css.secondaryBtn} onClick={() => fileInputRef.current?.click()}>Choose Image</button>
              <button type="button" className={css.primaryBtn} onClick={uploadPassport} disabled={uploading || !selectedFile}>
                {uploading ? <ThreeCircles height="18" width="18" color="#fff" visible /> : 'Upload Passport'}
              </button>
            </div>
            {photoError && <div className={css.inlineError}>{photoError}</div>}
            {photoSuccess && <div className={css.inlineSuccess}>{photoSuccess}</div>}
          </div>
        </div>
        {error && <div className={css.inlineError}>{error}</div>}
        {success && <div className={css.inlineSuccess}>{success}</div>}
        <form onSubmit={emailChangeStep === 'code' ? handleVerifyEmailChange : handleSave}>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>First Name</label>
              <input className={css.formInput} value={form.firstName} onChange={set('firstName')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Last Name</label>
              <input className={css.formInput} value={form.lastName} onChange={set('lastName')} />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Phone</label>
              <input className={css.formInput} value={form.phone} onChange={set('phone')} placeholder="e.g. 080XXXXXXXX" />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Email</label>
              <input className={css.formInput} type="email" value={form.email} onChange={set('email')} required disabled={emailChangeStep === 'code'} />
              {emailChangeStep === 'code' && (
                <div className={css.emailChangePanel}>
                  <p className={css.emailChangeHint}>Enter the six-digit code sent to {pendingEmail}.</p>
                  <div className={css.emailChangeActions}>
                    <input
                      className={css.formInput}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      pattern="[0-9]{6}"
                      value={verificationCode}
                      onChange={event => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      required
                    />
                    <button type="button" className={css.primaryBtn} onClick={handleVerifyEmailChange} disabled={emailChangeSaving || verificationCode.length !== 6}>
                      {emailChangeSaving ? <ThreeCircles height="18" width="18" color="#fff" visible /> : 'Verify Email'}
                    </button>
                  </div>
                  <button type="button" className={css.linkButton} onClick={cancelEmailChange}>Use a different email</button>
                </div>
              )}
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Date of Birth</label>
              <input className={css.formInput} type="date" value={form.dob} onChange={set('dob')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Occupation & Place of Work</label>
              <input className={css.formInput} value={form.occupation} onChange={set('occupation')} />
            </div>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Full Address</label>
            <textarea className={css.formInput} rows={2} value={form.address} onChange={set('address')} />
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>City</label>
              <input className={css.formInput} value={form.city} onChange={set('city')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>State / Region</label>
              <input className={css.formInput} value={form.stateRegion} onChange={set('stateRegion')} />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Postal Code</label>
              <input className={css.formInput} value={form.postalCode} onChange={set('postalCode')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Country</label>
              <input className={css.formInput} value={form.country} onChange={set('country')} />
            </div>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Country of Origin</label>
            <input className={css.formInput} value={form.countryOfOrigin} onChange={set('countryOfOrigin')} />
          </div>
          <div className={css.profileFormDivider}>Other guardian details</div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Full Name</label>
              <input className={css.formInput} value={form.otherGuardianName} onChange={set('otherGuardianName')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Relationship</label>
              <input className={css.formInput} value={form.otherGuardianRelationship} onChange={set('otherGuardianRelationship')} />
            </div>
          </div>
          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Phone</label>
              <input className={css.formInput} value={form.otherGuardianPhone} onChange={set('otherGuardianPhone')} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Email</label>
              <input className={css.formInput} type="email" value={form.otherGuardianEmail} onChange={set('otherGuardianEmail')} />
            </div>
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Address</label>
            <textarea className={css.formInput} rows={2} value={form.otherGuardianAddress} onChange={set('otherGuardianAddress')} />
          </div>

          <button type="submit" className={css.primaryBtn} disabled={saving || emailChangeStep === 'code'}>
            {saving ? <ThreeCircles height="18" width="18" color="#fff" visible /> : 'Save Changes'}
          </button>
        </form>
      </div>

      <div className={css.card}>
        <div className={css.cardHeader}>
          <h2 className={css.cardTitle}>Change Password</h2>
        </div>
        {pwdError && <div className={css.inlineError}>{pwdError}</div>}
        {pwdSuccess && <div className={css.inlineSuccess}>{pwdSuccess}</div>}
        <form onSubmit={handleChangePassword}>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Current Password</label>
            <SchoolPasswordInput className={css.formInput} value={pwd.currentPassword} onChange={e => setPwd(p => ({ ...p, currentPassword: e.target.value }))} required />
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>New Password</label>
            <SchoolPasswordInput className={css.formInput} value={pwd.newPassword} onChange={e => setPwd(p => ({ ...p, newPassword: e.target.value }))} required minLength={8} />
          </div>
          <button type="submit" className={css.primaryBtn} disabled={pwdSaving}>
            {pwdSaving ? <ThreeCircles height="18" width="18" color="#fff" visible /> : 'Change Password'}
          </button>
        </form>
      </div>
    </>
  );
}

// ─── Main dashboard shell ──────────────────────────────────────────────────────

export default function ParentDashboard() {
  const [activeTab, setActiveTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    return NAV.some(item => item.key === requested) ? requested : 'overview';
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profile, setProfile]     = useState(null);
  const [children, setChildren]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [viewingChild, setViewingChild] = useState(null); // { membershipId, firstName, lastName, ... } or null
  const [resetTarget, setResetTarget] = useState(null);
  const navigate                  = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      parentFetch('/parent/profile').then(r => r.json()),
      parentFetch('/parent/children').then(r => r.json()),
    ]).then(([profileRes, childrenRes]) => {
      setProfile(profileRes.profile || null);
      setChildren(childrenRes.children || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('school-token');
    const actor = localStorage.getItem('school-actor');
    if (!token || actor !== 'parent') {
      navigate('/school', { replace: true });
      return;
    }
    load();
  }, [navigate, load]);

  const handleLogout = () => {
    localStorage.removeItem('school-token');
    localStorage.removeItem('school-actor');
    localStorage.removeItem('school-must-change-pwd');
    navigate('/school', { replace: true });
  };

  const handleStudentUpdated = updatedStudent => {
    setChildren(current => current.map(child => (
      String(child.membershipId) === String(updatedStudent.membershipId)
        ? { ...child, ...updatedStudent }
        : child
    )));
    setViewingChild(current => current && String(current.membershipId) === String(updatedStudent.membershipId)
      ? { ...current, ...updatedStudent }
      : current);
  };

  const segmentMap = {
    overview: <OverviewSegment profile={profile} students={children} loading={loading} onGoToChildren={() => setActiveTab('children')} onSelectChild={c => setViewingChild(c)} />,
    children: <ChildrenSegment students={children} loading={loading} onSelectChild={c => setViewingChild(c)} onResetPassword={c => setResetTarget(c)} />,
    fees:     <FeesSegment />,
    calendar: <CalendarSegment profile={profile} />,
    timetable: <TimetableGrid mode="single" students={children} fetchFn={parentFetch} basePath="/parent" term={profile?.term} session={profile?.session} managementEnabled={profile?.subscription?.plan === 'premium' && profile?.subscription?.schoolManagementEnabled !== false} />,
    attendance: <AttendanceSegment profile={profile} students={children} />,
    messages: (
      <MessagingSegment
        fetchFn={parentFetch}
        basePath="/parent"
        actorType="parent"
        currentActorId={profile?._id}
        managementEnabled={profile?.subscription?.plan === 'premium' && profile?.subscription?.schoolManagementEnabled !== false}
      />
    ),
    profile:  <ProfileSegment key={profile?._id || 'profile-loading'} profile={profile} onProfileUpdated={updated => setProfile(prev => ({ ...prev, ...updated }))} />,
  };

  const activeNav = NAV.find(n => n.key === activeTab) || NAV[0];
  const topbarTitle = viewingChild ? 'Exams & Performance' : activeNav.label;

  return (
    <div className={css.shell}>
      {mobileNavOpen && <div className={css.navOverlay} onClick={() => setMobileNavOpen(false)} />}
      <aside className={`${css.sidebar} ${mobileNavOpen ? css.sidebarOpen : ''}`}>
        <div className={css.sidebarLogo}>
          <img src={profile?.schoolLogo || '/favicon.ico'} alt={profile?.schoolName || 'Bubble Ai Schools'} className={css.sidebarLogoImg} />
          <span className={css.sidebarLogoText}>{profile?.schoolName || 'Bubble Ai Schools'}</span>
        </div>

        <nav>
          {NAV.map(({ key, label, Icon }) => (
            <div
              key={key}
              className={`${css.navItem} ${activeTab === key && !viewingChild ? css.navItemActive : ''}`}
              onClick={() => { setActiveTab(key); setViewingChild(null); setMobileNavOpen(false); }}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && setActiveTab(key)}
            >
              <Icon className={css.navIcon} />
              {label}
            </div>
          ))}
        </nav>

        <div className={css.sidebarSpacer} />

        <div className={css.sidebarFooter}>
          <button className={css.logoutBtn} onClick={handleLogout}>
            <LogoutIcon style={{ fontSize: '1.1rem' }} />
            Log Out
          </button>
        </div>
      </aside>

      <main className={css.main}>
        <header className={css.topbar}>
          <div className={css.topbarLeft}>
            <button className={css.hamburgerBtn} onClick={() => setMobileNavOpen(true)} aria-label="Open menu">
              <MenuIcon fontSize="small" />
            </button>
            <h1 className={css.topbarTitle}>{topbarTitle}</h1>
          </div>
          <div className={css.topbarRight}>
            {profile && (
              <div className={css.parentBadge}>
                <div className={css.avatarInitials}>{initials(profile.firstName, profile.lastName)}</div>
                {profile.firstName} {profile.lastName}
              </div>
            )}
            <SchoolNotificationCenter inline />
          </div>
        </header>

        <div className={css.pageBody}>
          {activeTab === 'overview' && !viewingChild && <SchoolPWAInstallBanner />}
          {viewingChild ? (
            <ChildExamPerformance
              key={String(viewingChild.membershipId)}
              membershipId={viewingChild.membershipId}
              student={viewingChild}
              fetchFn={parentFetch}
              onBack={() => setViewingChild(null)}
              onStudentUpdated={handleStudentUpdated}
            />
          ) : (
            segmentMap[activeTab] || segmentMap.overview
          )}
          <SchoolPoweredFooter role="parent" />
        </div>
      </main>
      {resetTarget && <ResetChildPasswordModal student={resetTarget} onClose={() => setResetTarget(null)} />}
    </div>
  );
}
